import { useEffect } from 'react';
import { useMutation } from 'react-query';
import { useForm } from 'react-hook-form';
import styled from '@emotion/styled';
import { Button, useNotify, Text, tokens, Field, TextInput, Modal } from '@hotjar/hotsauce';

import { queryClient } from '@hotjar/webapp/utils/react-query';
import { useOrganizations, ORGANIZATIONS_QUERY_KEY } from '@hotjar/organization';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useCurrentUser } from '@hotjar/user';
import { Select } from '@hotjar/webapp/components/Select';
import { bus, refreshData } from '@hotjar/webapp/utils/events';
import { useAuthorization } from '@hotjar/webapp/domains/authorization';
import { useModalEvents } from '../../useModalEvents';
import { Event, UiPosition } from '../../events';
import type { SiteModel } from '../types';
import * as api from './api';

interface Props {
  handleSuccess(): void;
  handleCancel(): void;
  opened: boolean;
  site: Pick<SiteModel, 'id' | 'effectivePlan' | 'organizationId' | 'account'>;
}

interface FormInputs {
  organizationId: string;
  newOrganizationName?: string;
}

const TRANSLATION_DOMAIN = 'account:transfer-site';
const FORM_ID = 'transferSite';
const SELECT_ORG_ID = 'transferToOrganization';
const SELECT_NEW_ORGANIZATION_VALUE = 'newOrganization';
const NEW_ORGANIZATION_INPUT_ID = 'newOrganizationName';

const S = {
  Field: styled(Field)`
    margin-bottom: ${tokens.spacer.x3};
    width: 100%;
  `,
};

export function TransferSiteModal({
  handleSuccess,
  handleCancel,
  opened,
  site,
}: Props): React.ReactElement {
  const { t } = useTranslationWithPrefix(TRANSLATION_DOMAIN);
  const { showToast } = useNotify();
  const { data: currentUser } = useCurrentUser();
  const { data: organizations } = useOrganizations(currentUser?.id);
  const { trackClicked, trackOpened } = useModalEvents({
    organizationId: site.organizationId,
    accountId: site.account.id as number,
    siteId: site.id,
    confirm: Event.CONFIRM_TRANSFER_SITE_MODAL,
    uiPosition: UiPosition.TRANSFER_SITE_MODAL,
    opened: Event.VIEW_TRANSFER_SITE_MODAL,
  });
  const { hasPermission } = useAuthorization();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormInputs>();

  const currentOrg = organizations?.find(({ id }) => id === site.organizationId);
  const creatingNewOrg = watch('organizationId') === SELECT_NEW_ORGANIZATION_VALUE;

  useEffect(() => {
    trackOpened(opened);
  }, [opened, trackOpened]);

  const mutation = useMutation(
    async (data: FormInputs) => {
      if (!currentOrg) {
        return Promise.reject();
      }

      if (data.newOrganizationName !== undefined) {
        return api.createOrgAndTransferSite({
          accountId: currentOrg.accountId,
          siteId: site.id,
          organizationName: data.newOrganizationName,
        });
      }

      return api.transferSite({
        accountId: currentOrg.accountId,
        organizationId: window.parseInt(data.organizationId),
        siteId: site.id,
      });
    },
    {
      onSuccess: async () => {
        showToast({
          text: t('transfer-success'),
          kind: 'info',
        });
        bus.publish(
          refreshData(() => {
            queryClient.invalidateQueries(ORGANIZATIONS_QUERY_KEY);
          }),
        );
        handleSuccess();
      },
      onError: () => {
        showToast({ text: t('transfer-failure'), kind: 'danger' });
      },
    },
  );

  function validateTransferSite(value: string) {
    if (value === SELECT_NEW_ORGANIZATION_VALUE) {
      return true;
    }
    const newOrg = organizations?.find(({ id }) => id.toString() === value);
    const movingToAnotherAccount = newOrg?.accountId !== currentOrg?.accountId;

    if (movingToAnotherAccount && site.effectivePlan !== 'basic') {
      return t('error-cannot-transfer-out-paid-site');
    }

    return true;
  }

  return (
    <Modal isOpen={opened} size="m" contentLabel={t('title')} onClose={() => handleCancel()}>
      <Modal.Header title={t('title')} />
      <Modal.Body>
        <Text as="p">{t('description')}</Text>
        <form
          onSubmit={handleSubmit((data) => {
            trackClicked();
            mutation.mutate(data);
          })}
          id={FORM_ID}
        >
          <S.Field
            labelText={t('organization')}
            inputId={SELECT_ORG_ID}
            isRequired
            isInvalid={!!errors.organizationId}
            invalidText={errors.organizationId?.message}
          >
            <Select
              id={SELECT_ORG_ID}
              ref={register({
                required: t<string>('required'),
                validate: {
                  validateTransferSite,
                },
              })}
              name="organizationId"
              isError={!!errors.organizationId}
            >
              <option value="">{t('select-placeholder')}</option>
              {currentUser?.isAccountAdmin && (
                <>
                  <option disabled>──────────</option>
                  <option value={SELECT_NEW_ORGANIZATION_VALUE}>{t('new-organization')}</option>
                  <option disabled>──────────</option>
                </>
              )}
              {organizations
                ?.filter(
                  /**
                   * The following logic used in the filter function is taken from the back-end code.
                   * _transfer_site_to_organization(...) @ site_api.py
                   * @param id
                   */
                  ({ id }) =>
                    hasPermission('site', `${site.id}`, 'site-transfer-to-org') &&
                    hasPermission('organization', `${id}`, 'org-update'),
                )
                .map(({ id, name }) => (
                  <option value={id} key={id} disabled={id === site.organizationId}>
                    {name}
                  </option>
                ))}
            </Select>
          </S.Field>
          {creatingNewOrg && (
            <S.Field
              labelText={t('organization-name')}
              invalidText={errors.newOrganizationName?.message}
              inputId={NEW_ORGANIZATION_INPUT_ID}
              isInvalid={!!errors.newOrganizationName}
              isRequired
            >
              <TextInput
                id={NEW_ORGANIZATION_INPUT_ID}
                name="newOrganizationName"
                ref={register({
                  required: t<string>('required'),
                })}
                isInvalid={!!errors.newOrganizationName}
              />
            </S.Field>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" onClick={() => handleCancel()} isDisabled={mutation.isLoading}>
          {t('cancel')}
        </Button>
        <Button kind="primary" type="submit" form={FORM_ID} isLoading={mutation.isLoading}>
          {t('transfer')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
